/**
 * Push Notifications Service
 * 
 * Servicio para gestionar notificaciones push en iOS/Android.
 * REQUERIDO para aprobación de Apple App Store (Directriz 4.2)
 * 
 * @module native/push-notifications
 */

import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { LocalNotifications, ScheduleOptions } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { Logger } from '@/core/utils';

const logger = Logger.getLogger('PushNotificationsService');

export interface NotificationData {
  title: string;
  body: string;
  data?: Record<string, unknown>;
  id?: number;
}

export interface ScheduledNotification {
  id: number;
  title: string;
  body: string;
  scheduleAt: Date;
  extra?: Record<string, unknown>;
}

class PushNotificationsService {
  private static instance: PushNotificationsService;
  private isInitialized = false;
  private pushToken: string | null = null;
  private listeners: Map<string, (data: unknown) => void> = new Map();

  private constructor() {}

  static getInstance(): PushNotificationsService {
    if (!PushNotificationsService.instance) {
      PushNotificationsService.instance = new PushNotificationsService();
    }
    return PushNotificationsService.instance;
  }

  /**
   * Verifica si estamos en un dispositivo nativo
   */
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Inicializa el servicio de notificaciones push
   */
  async initialize(): Promise<boolean> {
    if (!this.isNativePlatform()) {
      logger.info('Push notifications no disponibles en web');
      return false;
    }

    if (this.isInitialized) {
      return true;
    }

    try {
      // Verificar permisos actuales
      let permStatus = await PushNotifications.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        logger.warn('Permisos de push notifications denegados');
        return false;
      }

      // Registrar para recibir push
      await PushNotifications.register();

      // Configurar listeners
      this.setupListeners();

      // Inicializar también notificaciones locales
      await this.initializeLocalNotifications();

      this.isInitialized = true;
      logger.info('Push notifications inicializadas correctamente');
      return true;
    } catch (error) {
      logger.error('Error inicializando push notifications', { error });
      return false;
    }
  }

  /**
   * Inicializa notificaciones locales (recordatorios)
   */
  private async initializeLocalNotifications(): Promise<void> {
    try {
      const permStatus = await LocalNotifications.requestPermissions();
      if (permStatus.display !== 'granted') {
        logger.warn('Permisos de notificaciones locales denegados');
      }
    } catch (error) {
      logger.error('Error inicializando notificaciones locales', { error });
    }
  }

  /**
   * Configura los listeners de eventos
   */
  private setupListeners(): void {
    // Token recibido
    PushNotifications.addListener('registration', (token: Token) => {
      this.pushToken = token.value;
      logger.info('Push token recibido', { token: token.value.substring(0, 20) + '...' });
      this.notifyListeners('tokenReceived', token.value);
    });

    // Error de registro
    PushNotifications.addListener('registrationError', (error) => {
      logger.error('Error en registro de push', { error });
      this.notifyListeners('registrationError', error);
    });

    // Notificación recibida en foreground
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      logger.info('Notificación recibida', { 
        title: notification.title,
        id: notification.id 
      });
      this.notifyListeners('notificationReceived', notification);
    });

    // Usuario interactuó con notificación
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      logger.info('Acción de notificación', { 
        actionId: action.actionId,
        notification: action.notification.title 
      });
      this.notifyListeners('notificationAction', action);
    });
  }

  /**
   * Obtiene el token de push actual
   */
  getToken(): string | null {
    return this.pushToken;
  }

  /**
   * Programa una notificación local (recordatorio de práctica)
   */
  async scheduleLocalNotification(notification: ScheduledNotification): Promise<boolean> {
    if (!this.isNativePlatform()) {
      logger.info('Notificaciones locales no disponibles en web');
      return false;
    }

    try {
      const scheduleOptions: ScheduleOptions = {
        notifications: [
          {
            id: notification.id,
            title: notification.title,
            body: notification.body,
            schedule: { at: notification.scheduleAt },
            extra: notification.extra,
            sound: 'default',
            smallIcon: 'ic_stat_notify',
            largeIcon: 'ic_launcher',
          },
        ],
      };

      await LocalNotifications.schedule(scheduleOptions);
      logger.info('Notificación local programada', { 
        id: notification.id,
        scheduleAt: notification.scheduleAt 
      });
      return true;
    } catch (error) {
      logger.error('Error programando notificación local', { error });
      return false;
    }
  }

  /**
   * Programa recordatorio de práctica diaria
   */
  async schedulePracticeReminder(
    practiceId: string,
    practiceName: string,
    reminderTime: Date
  ): Promise<boolean> {
    const notificationId = this.generateNotificationId(practiceId);
    
    return this.scheduleLocalNotification({
      id: notificationId,
      title: '🧘 Hora de tu práctica',
      body: `Es momento de realizar: ${practiceName}`,
      scheduleAt: reminderTime,
      extra: {
        type: 'practice_reminder',
        practiceId,
      },
    });
  }

  /**
   * Cancela una notificación programada
   */
  async cancelNotification(notificationId: number): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      await LocalNotifications.cancel({ notifications: [{ id: notificationId }] });
      logger.info('Notificación cancelada', { id: notificationId });
    } catch (error) {
      logger.error('Error cancelando notificación', { error });
    }
  }

  /**
   * Cancela todas las notificaciones pendientes
   */
  async cancelAllNotifications(): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({ notifications: pending.notifications });
        logger.info('Todas las notificaciones canceladas', { count: pending.notifications.length });
      }
    } catch (error) {
      logger.error('Error cancelando notificaciones', { error });
    }
  }

  /**
   * Obtiene notificaciones pendientes
   */
  async getPendingNotifications(): Promise<number[]> {
    if (!this.isNativePlatform()) return [];

    try {
      const pending = await LocalNotifications.getPending();
      return pending.notifications.map(n => n.id);
    } catch (error) {
      logger.error('Error obteniendo notificaciones pendientes', { error });
      return [];
    }
  }

  /**
   * Registra un listener para eventos
   */
  addListener(event: string, callback: (data: unknown) => void): void {
    this.listeners.set(event, callback);
  }

  /**
   * Elimina un listener
   */
  removeListener(event: string): void {
    this.listeners.delete(event);
  }

  /**
   * Notifica a los listeners registrados
   */
  private notifyListeners(event: string, data: unknown): void {
    const callback = this.listeners.get(event);
    if (callback) {
      callback(data);
    }
  }

  /**
   * Genera un ID único para notificaciones basado en practiceId
   */
  private generateNotificationId(practiceId: string): number {
    let hash = 0;
    for (let i = 0; i < practiceId.length; i++) {
      const char = practiceId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Limpia recursos al destruir
   */
  async cleanup(): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      await PushNotifications.removeAllListeners();
      await LocalNotifications.removeAllListeners();
      this.listeners.clear();
      this.isInitialized = false;
      logger.info('Push notifications cleanup completado');
    } catch (error) {
      logger.error('Error en cleanup de push notifications', { error });
    }
  }
}

export const pushNotificationsService = PushNotificationsService.getInstance();










